import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { getGisToken } from '@app/modules/gis/gis-token';
import { GIS_PORTAL_BASE } from '@app/modules/gis/gis-config';

/**
 * Appends the GIS Portal access token as a `token` query parameter to every
 * HttpClient request targeting gis.xom.cloud (portal, MapServer, FeatureServer
 * REST endpoints). Awaits a silent refresh if the token is expired so the
 * request always uses a valid token.
 */
@Injectable()
export class GisTokenInterceptor implements HttpInterceptor {
  private readonly gisPortalOrigin = new URL(GIS_PORTAL_BASE).origin;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isGisPortalRequest(request.url)) {
      return next.handle(request);
    }

    return from(getGisToken()).pipe(
      switchMap(token => {
        if (!token) {
          return next.handle(request);
        }
        const cloned = request.clone({
          params: request.params.set('token', token)
        });
        return next.handle(cloned);
      })
    );
  }

  private isGisPortalRequest(url: string): boolean {
    try {
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      return new URL(url, currentOrigin).origin === this.gisPortalOrigin;
    } catch {
      return false;
    }
  }
}
